import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    useTheme,
    alpha
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Today as TodayIcon,
    Event as EventIcon,
    Person as PersonIcon,
    AccessTime as TimeIcon,
    Edit as EditIcon,
    Add as AddIcon
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { rosterService, Shift } from '../../services/rosterService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTimeForUserTimezone, getUserTimezone } from '../../utils/dateUtils';
import { getStaffDisplayName, getClientDisplayName } from '../../utils/nameUtils';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface CalendarDay {
    date: Dayjs;
    isCurrentWeek: boolean;
    shifts: Shift[];
}

interface RosterCalendarProps {
    onShiftClick?: (shift: Shift) => void;
    onDateClick?: (date: Dayjs) => void;
    onAddShift?: (date: Dayjs) => void;
    selectedStaffId?: number;
    readonly?: boolean;
    refreshTrigger?: number; // Increment this to trigger a refresh
    staffOptions?: Array<{ value: number; label: string }>;
    clientOptions?: Array<{ value: number; label: string }>;
}

const RosterCalendar: React.FC<RosterCalendarProps> = ({ 
    onShiftClick, 
    onDateClick, 
    onAddShift, 
    selectedStaffId, 
    readonly = false, 
    refreshTrigger = 0,
    staffOptions = [],
    clientOptions = []
}) => {
    const theme = useTheme();
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(false);
    const [shiftDetailsOpen, setShiftDetailsOpen] = useState(false);
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

    // Helper function to get staff name by ID using global utility
    const getStaffName = (staffId: number): string => {
        const staff = staffOptions.find(s => s.value === staffId);
        return getStaffDisplayName(staff ? { id: staffId, label: staff.label } : null, `Staff ${staffId}`);
    };

    // Helper function to get client name by ID using global utility
    const getClientName = (clientId: number): string => {
        const client = clientOptions.find(c => c.value === clientId);
        return getClientDisplayName(client ? { id: clientId, label: client.label } : null, `Client ${clientId}`);
    };

    const statusColors = {
        scheduled: 'primary',
        in_progress: 'warning',
        completed: 'success',
        cancelled: 'error',
        no_show: 'secondary'
    } as const;

    // Calculate weekly bounds - show current week
    const startOfWeek = currentDate.startOf('week'); // Sunday
    const endOfWeek = startOfWeek.add(6, 'day'); // Saturday

    const calendarStart = startOfWeek;
    const calendarEnd = endOfWeek;

    useEffect(() => {
        loadShifts();
    }, [currentDate, selectedStaffId, refreshTrigger, staffOptions, clientOptions]);

    const loadShifts = async () => {
        // Don't load shifts if staff/client options aren't loaded yet
        if (staffOptions.length === 0 || clientOptions.length === 0) {
            return;
        }

        setLoading(true);
        try {
            const startDate = calendarStart.format('YYYY-MM-DD');
            const endDate = calendarEnd.format('YYYY-MM-DD');
            
            const data = await rosterService.getShiftsByDateRange(startDate, endDate);
            
            // Ensure data is an array and filter by staff if specified
            const shiftsArray = Array.isArray(data) ? data : [];
            const filteredShifts = selectedStaffId 
                ? shiftsArray.filter(shift => shift.staffId === selectedStaffId)
                : shiftsArray;

            // Populate staff and client names
            const shiftsWithNames = filteredShifts.map(shift => ({
                ...shift,
                staffName: shift.staffName || getStaffName(shift.staffId),
                clientName: shift.clientName || getClientName(shift.clientId)
            }));
                
            setShifts(shiftsWithNames);
        } catch (error) {
            console.error('Error loading shifts:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateCalendarDays = (): CalendarDay[] => {
        const days: CalendarDay[] = [];
        let currentDay = calendarStart;

        while (currentDay.isBefore(calendarEnd, 'day') || currentDay.isSame(calendarEnd, 'day')) {
            const userTimezone = getUserTimezone();
            const dayShifts = shifts.filter(shift => 
                dayjs(shift.startDateTime).tz(userTimezone).format('YYYY-MM-DD') === currentDay.format('YYYY-MM-DD')
            );

            days.push({
                date: currentDay,
                isCurrentWeek: currentDay.isSame(currentDate, 'week'),
                shifts: dayShifts
            });

            currentDay = currentDay.add(1, 'day');
        }

        return days;
    };

    const calendarDays = generateCalendarDays();

    const handleShiftClick = (shift: Shift) => {
        // Show shift details dialog only
        setSelectedShift(shift);
        setShiftDetailsOpen(true);
        
        // Don't call the parent handler here - we'll call it from the Edit button
    };

    const handleDateClick = (date: Dayjs) => {
        if (onDateClick) {
            onDateClick(date);
        }
    };

    const handleAddShift = (date: Dayjs) => {
        if (onAddShift) {
            onAddShift(date);
        }
    };

    const getStatusColor = (status: string) => {
        return statusColors[status as keyof typeof statusColors] || 'default';
    };

    const renderShiftCard = (shift: Shift) => {
        // Use timezone-aware formatting for shift times
        const userTimezone = getUserTimezone();
        const startTime = dayjs(shift.startDateTime).tz(userTimezone).format('HH:mm');
        const endTime = dayjs(shift.endDateTime).tz(userTimezone).format('HH:mm');
        const staffName = shift.staffName || getStaffName(shift.staffId);
        const clientName = shift.clientName || getClientName(shift.clientId);
        const duration = shift.durationHours || dayjs(shift.endDateTime).tz(userTimezone).diff(dayjs(shift.startDateTime).tz(userTimezone), 'hour', true);
        
        return (
            <Card
                key={shift.id}
                sx={{
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        transform: 'translateY(-1px)',
                        boxShadow: 3
                    },
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    borderRadius: 1,
                    transition: 'all 0.2s ease-in-out'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    handleShiftClick(shift);
                }}
            >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Time and Duration */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {startTime} - {endTime}
                        </Typography>
                        <Chip
                            label={`${duration.toFixed(1)}h`}
                            size="small"
                            sx={{ 
                                fontSize: '0.65rem', 
                                height: 18,
                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                                color: 'info.main'
                            }}
                        />
                    </Box>
                    
                    {/* Staff */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                            {staffName}
                        </Typography>
                    </Box>
                    
                    {/* Client */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EventIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {clientName}
                        </Typography>
                    </Box>
                    
                    {/* Status and Rate */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                            label={shift.status?.replace('_', ' ').toUpperCase()}
                            color={getStatusColor(shift.status)}
                            size="small"
                            sx={{ fontSize: '0.6rem', height: 20 }}
                        />
                        <Typography variant="caption" sx={{ 
                            fontSize: '0.7rem', 
                            color: 'success.main',
                            fontWeight: 'bold'
                        }}>
                            ${shift.hourlyRate}/hr
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const renderDay = (day: CalendarDay) => {
        const isToday = day.date.isSame(dayjs(), 'day');
        const isCurrentWeek = day.isCurrentWeek;
        const isWeekend = day.date.day() === 0 || day.date.day() === 6;
        
        return (
            <Box
                sx={{
                    minHeight: 400,
                    border: 1,
                    borderColor: isToday ? 'primary.main' : 'divider',
                    borderWidth: isToday ? 2 : 1,
                    backgroundColor: isWeekend 
                        ? alpha(theme.palette.grey[100], 0.5)
                        : 'background.paper',
                    position: 'relative',
                    p: 1,
                    cursor: readonly ? 'default' : 'pointer',
                    '&:hover': !readonly ? {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: 'primary.light'
                    } : {},
                    transition: 'all 0.2s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={() => !readonly && handleDateClick(day.date)}
            >
                {/* Date header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: isToday ? 'bold' : 'medium',
                                color: isToday 
                                    ? 'primary.main' 
                                    : 'text.primary',
                                fontSize: '1.1rem'
                            }}
                        >
                            {day.date.format('D')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: isToday ? 'primary.main' : 'text.secondary',
                                fontSize: '0.75rem',
                                fontWeight: isToday ? 'medium' : 'normal'
                            }}
                        >
                            {day.date.format('ddd')}
                        </Typography>
                    </Box>
                    
                    {!readonly && (
                        <Tooltip title="Add shift">
                            <IconButton
                                size="small"
                                sx={{ 
                                    opacity: 0.3, 
                                    '&:hover': { opacity: 1 },
                                    color: 'primary.main'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddShift(day.date);
                                }}
                            >
                                <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* Shifts - Scrollable container */}
                <Box 
                    sx={{ 
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: alpha(theme.palette.grey[300], 0.3),
                            borderRadius: '2px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: alpha(theme.palette.primary.main, 0.3),
                            borderRadius: '2px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: alpha(theme.palette.primary.main, 0.5),
                        },
                    }}
                >
                    {day.shifts.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100px',
                                color: 'text.secondary',
                                fontStyle: 'italic'
                            }}
                        >
                            <Typography variant="caption">
                                No shifts scheduled
                            </Typography>
                        </Box>
                    ) : (
                        day.shifts.map(shift => renderShiftCard(shift))
                    )}
                </Box>

                {/* Shift count badge for many shifts */}
                {day.shifts.length > 5 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            zIndex: 1
                        }}
                    >
                        {day.shifts.length}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box>
            {/* Calendar Header */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3,
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton 
                        onClick={() => setCurrentDate(currentDate.subtract(1, 'week'))}
                        sx={{ 
                            backgroundColor: 'background.paper',
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ textAlign: 'center', minWidth: 300 }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 'bold',
                            color: 'primary.main'
                        }}>
                            {startOfWeek.format('MMMM YYYY')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.9rem'
                        }}>
                            {startOfWeek.format('MMM D')} - {endOfWeek.format('MMM D, YYYY')}
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={() => setCurrentDate(currentDate.add(1, 'week'))}
                        sx={{ 
                            backgroundColor: 'background.paper',
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<TodayIcon />}
                        onClick={() => setCurrentDate(dayjs())}
                        size="small"
                        sx={{ borderRadius: 2 }}
                    >
                        This Week
                    </Button>
                </Box>
            </Box>

            {/* Calendar Grid */}
            <Box sx={{ overflowX: 'auto' }}>
                {/* Day headers */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(7, minmax(200px, 1fr))', 
                    gap: 1,
                    mb: 1,
                    minWidth: '1400px' // Ensure minimum width for proper display
                }}>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                        const dayDate = startOfWeek.add(index, 'day');
                        const isToday = dayDate.isSame(dayjs(), 'day');
                        const isWeekend = index === 0 || index === 6;
                        
                        return (
                            <Box key={day} sx={{ 
                                p: 1.5, 
                                textAlign: 'center',
                                backgroundColor: isToday 
                                    ? 'primary.main'
                                    : isWeekend
                                        ? alpha(theme.palette.warning.main, 0.1)
                                        : alpha(theme.palette.primary.main, 0.1),
                                borderRadius: 1,
                                border: `1px solid ${isToday 
                                    ? theme.palette.primary.main
                                    : alpha(theme.palette.primary.main, 0.2)}`,
                                color: isToday ? 'primary.contrastText' : 'inherit'
                            }}>
                                <Typography variant="body2" sx={{ 
                                    fontWeight: 'bold', 
                                    fontSize: '0.9rem'
                                }}>
                                    {day.toUpperCase()}
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                    fontSize: '1.1rem',
                                    fontWeight: isToday ? 'bold' : 'medium'
                                }}>
                                    {dayDate.format('D')}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                    fontSize: '0.7rem',
                                    opacity: 0.8
                                }}>
                                    {dayDate.format('MMM')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Calendar days */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(7, minmax(200px, 1fr))', 
                    gap: 1,
                    backgroundColor: alpha(theme.palette.grey[100], 0.3),
                    p: 1,
                    borderRadius: 2,
                    minWidth: '1400px' // Ensure minimum width for proper display
                }}>
                    {calendarDays.map((day, index) => (
                        <Box key={index}>
                            {renderDay(day)}
                        </Box>
                    ))}
                </Box>
            </Box>

            {loading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    zIndex: 1000
                }}>
                    <Typography>Loading shifts...</Typography>
                </Box>
            )}

            {/* Shift Details Dialog */}
            <Dialog
                open={shiftDetailsOpen}
                onClose={() => setShiftDetailsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <EventIcon color="primary" />
                        Shift Details
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedShift && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    📅 Schedule
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Date:</strong> {dayjs(selectedShift.startDateTime).tz(getUserTimezone()).format('dddd, MMMM D, YYYY')}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Time:</strong> {dayjs(selectedShift.startDateTime).tz(getUserTimezone()).format('h:mm A')} - {dayjs(selectedShift.endDateTime).tz(getUserTimezone()).format('h:mm A')}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Duration:</strong> {selectedShift.durationHours || 'N/A'} hours
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    👥 People
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Staff:</strong> {selectedShift.staffName || getStaffName(selectedShift.staffId)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Client:</strong> {selectedShift.clientName || getClientName(selectedShift.clientId)}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    📋 Details
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Type:</strong> {selectedShift.shiftType?.replace('_', ' ')?.toUpperCase() || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Status:</strong>{' '}
                                    <Chip
                                        label={selectedShift.status?.replace('_', ' ').toUpperCase()}
                                        color={getStatusColor(selectedShift.status)}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Hourly Rate:</strong> ${selectedShift.hourlyRate || 'N/A'}
                                </Typography>
                                {selectedShift.totalAmount && (
                                    <Typography variant="body2">
                                        <strong>Total Amount:</strong> ${selectedShift.totalAmount}
                                    </Typography>
                                )}
                            </Box>

                            {(selectedShift.careInstructions || selectedShift.notes) && (
                                <Box>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        📝 Notes
                                    </Typography>
                                    {selectedShift.careInstructions && (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Care Instructions:</strong> {selectedShift.careInstructions}
                                        </Typography>
                                    )}
                                    {selectedShift.notes && (
                                        <Typography variant="body2">
                                            <strong>Notes:</strong> {selectedShift.notes}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {selectedShift.specialRequirements && (
                                <Box>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        ⚠️ Special Requirements
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedShift.specialRequirements}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShiftDetailsOpen(false)}>
                        Close
                    </Button>
                    {!readonly && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setShiftDetailsOpen(false);
                                if (onShiftClick && selectedShift) {
                                    onShiftClick(selectedShift);
                                }
                            }}
                        >
                            Edit Shift
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RosterCalendar;
